import React from 'react';
import { Sparkles, Target, Zap } from 'lucide-react';
import { statsData } from '../data/mock';

export const About = () => {
  return (
    <section id="about" className="relative py-24 bg-gunmetal-950">
      {/* Background texture */}
      <div className="absolute inset-0 opacity-5 noise-texture"></div>
      
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16 animate-fade-in-up">
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-black mb-4">
            <span className="text-chrome">WHERE </span>
            <span className="text-accent-red">ART</span>
            <span className="text-chrome"> MEETS </span>
            <span className="text-accent-red">INSPIRATION</span>
          </h2>
          <div className="w-24 h-1 bg-accent-red mx-auto mb-6"></div>
          <p className="text-xl text-steel-400 max-w-3xl mx-auto leading-relaxed mb-4">
            At MINDESIGN, we believe every individual has the power to redesign their life narrative.
          </p>
          <p className="text-2xl md:text-3xl font-bold text-metallic-ice">
            LIVE MORE • LOVE MORE • LAUGH MORE • GIVE MORE
          </p>
        </div>

        {/* Main Content Grid */}
        <div className="grid md:grid-cols-2 gap-12 mb-20">
          {/* Left Column - Image */}
          <div className="relative group">
            <div className="overflow-hidden rounded-lg shadow-2xl">
              <img 
                src="https://images.pexels.com/photos/4031037/pexels-photo-4031037.jpeg"
                alt="MINDESIGN Creative Process"
                className="w-full h-[500px] object-cover transition-transform duration-700 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-gunmetal-950 via-transparent to-transparent"></div>
            </div>
            {/* Accent Border */}
            <div className="absolute -bottom-4 -right-4 w-full h-full border-2 border-accent-red rounded-lg -z-10"></div>
          </div>

          {/* Right Column - Content */}
          <div className="flex flex-col justify-center space-y-6">
            <div className="space-y-4">
              <div className="flex items-start space-x-4 group hover:translate-x-2 transition-transform duration-300">
                <div className="p-3 bg-accent-red/10 rounded-lg">
                  <Sparkles className="text-accent-red" size={24} />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-steel-200 mb-2">Over 30 Original Stories</h3>
                  <p className="text-steel-400 leading-relaxed">
                    From the raw honesty of the Meth Chronicles trilogy to the spiritual journey 
                    of God's Way vs. My Way, spanning TV series, movies, books, and comics. 
                    30 years of storytelling mastery from the MINDESIGN ENTERPRISES VAULT.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4 group hover:translate-x-2 transition-transform duration-300">
                <div className="p-3 bg-accent-red/10 rounded-lg">
                  <Zap className="text-accent-red" size={24} />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-steel-200 mb-2">200+ Songs & Soundscapes</h3>
                  <p className="text-steel-400 leading-relaxed">
                    Over 150 original compositions providing the soundtrack to a life of resilience. 
                    Music that moves the soul and fuels transformation across genres and decades.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4 group hover:translate-x-2 transition-transform duration-300">
                <div className="p-3 bg-accent-red/10 rounded-lg">
                  <Target className="text-accent-red" size={24} />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-steel-200 mb-2">Empowerment Philosophy</h3>
                  <p className="text-steel-400 leading-relaxed">
                    Practical wisdom based on our signature "Four Pillars" to help you navigate 
                    life's challenges with grace and purpose.
                  </p>
                </div>
              </div>
            </div>

            <div className="pt-6">
              <p className="text-2xl font-bold text-steel-200 italic border-l-4 border-accent-red pl-4">
                "Whether you're here to explore a new story, listen to soulful melodies, 
                or find the motivation to start your next chapter, MINDESIGN ENTERPRISES 
                is your partner in creative and personal growth."
              </p>
            </div>
          </div>
        </div>

        {/* Stats Section */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
          {statsData.map((stat, index) => (
            <div 
              key={index}
              className="text-center p-6 bg-gunmetal-900/50 backdrop-blur-sm rounded-lg border border-steel-700/30 hover:border-accent-red/50 transition-all duration-300 hover:transform hover:scale-105"
            >
              <div className="text-4xl md:text-5xl font-black text-accent-red mb-2">
                {stat.value}
              </div>
              <div className="text-steel-200 font-bold mb-1">{stat.label}</div>
              <div className="text-sm text-steel-500">{stat.description}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
